import { roundNumber } from './rounding';

function safeNumber(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
}

const UNKNOWN_EMPLOYEE_NAME = 'Неизвестный сотрудник';

function resolveEmployeeReference(employeesById = null, employeeId = '') {
  if (!employeesById || typeof employeesById !== 'object') return null;

  const employeeKey = employeeId == null ? '' : String(employeeId);
  return (
    employeesById[employeeKey] ||
    Object.values(employeesById).find((employee) => String(employee?.employeeExternalId ?? '') === employeeKey) ||
    null
  );
}

function resolveKnownEmployeeName(...names) {
  return (
    names.find((name) => {
      const normalized = String(name || '').trim();
      return normalized && normalized !== UNKNOWN_EMPLOYEE_NAME;
    }) || UNKNOWN_EMPLOYEE_NAME
  );
}

function average(values = []) {
  if (!values.length) return 0;
  return roundNumber(values.reduce((sum, value) => sum + safeNumber(value), 0) / values.length, 2);
}

function joinTitles(values = []) {
  const unique = [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean).filter((v) => v !== '—'))];
  return unique.length ? unique.join(', ') : '—';
}

function getUniqueTitles(values = []) {
  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean).filter((v) => v !== '—'))];
}

function resolveEmployeePointContextTitle(employeeRecords = [], ratingRow = null, options = {}) {
  const selectedPointIds = Array.isArray(options.selectedPointIds) ? options.selectedPointIds : [];
  const titles = getUniqueTitles([
    ...employeeRecords.map((record) => record?.pointTitle),
    ...(selectedPointIds.length === 1 ? [ratingRow?.pointTitle] : []),
  ]);

  if (!titles.length) return '—';
  if (!selectedPointIds.length && titles.length > 3) return 'Все точки';
  return titles.join(', ');
}

export function groupRecordsByEmployee(records = []) {
  return records.reduce((acc, record) => {
    const employeeId = record?.employeeId == null || record?.employeeId === '' ? 'unknown' : String(record.employeeId);
    if (!acc[employeeId]) {
      acc[employeeId] = [];
    }
    acc[employeeId].push(record);
    return acc;
  }, {});
}

export function buildEmployeeSummary(records = []) {
  const revenue = records.reduce((sum, record) => sum + safeNumber(record?.netAmount), 0);
  const checksCount = records.length;
  const bonuses = records.reduce((sum, record) => sum + safeNumber(record?.bonusWriteOffAmount), 0);
  const ratings = records.map((record) => record?.guestRating).filter((value) => value != null);

  return {
    revenue,
    checksCount,
    averageCheck: checksCount ? roundNumber(revenue / checksCount, 2) : 0,
    bonuses,
    guestRating: average(ratings),
  };
}

function hasOwnMetric(row = null, field = '') {
  return Boolean(row && Object.prototype.hasOwnProperty.call(row, field));
}

function rowMetric(row = null, field = '', fallback = 0) {
  return hasOwnMetric(row, field) ? safeNumber(row?.[field]) : fallback;
}

function buildEmployeeSummaryFromRatingRow(ratingRow = null, fallbackSummary = {}) {
  if (!ratingRow) return fallbackSummary;

  const revenue = rowMetric(ratingRow, 'revenue', fallbackSummary.revenue);
  const checksCount = rowMetric(ratingRow, 'checksCount', fallbackSummary.checksCount);
  const bonuses = rowMetric(ratingRow, 'bonuses', fallbackSummary.bonuses);
  let averageCheck = fallbackSummary.averageCheck;
  if (hasOwnMetric(ratingRow, 'averageCheck')) {
    averageCheck = safeNumber(ratingRow.averageCheck);
  } else if (checksCount) {
    averageCheck = roundNumber(revenue / checksCount, 2);
  }

  let guestRating = fallbackSummary.guestRating;
  if (hasOwnMetric(ratingRow, 'guestRating')) {
    guestRating = ratingRow.guestRating == null ? null : safeNumber(ratingRow.guestRating);
  }

  return {
    revenue,
    checksCount,
    averageCheck,
    bonuses,
    guestRating,
  };
}

export function buildTopDish(records = []) {
  const byDish = records.reduce((acc, record) => {
    (record?.items || []).forEach((item) => {
      const key = item?.productId || item?.sku || item?.productName;
      if (!key) return;

      if (!acc[key]) {
        acc[key] = {
          key,
          name: item?.productName || 'Без названия',
          qty: 0,
        };
      }

      acc[key].qty += safeNumber(item?.qty);
    });

    return acc;
  }, {});

  const values = Object.values(byDish);
  if (!values.length) {
    return {
      name: 'Нет данных',
      qty: 0,
    };
  }

  return values.sort((left, right) => right.qty - left.qty)[0];
}

export function buildTopCheck(records = []) {
  const topCheck = records.reduce((max, record) => {
    return safeNumber(record?.netAmount) > safeNumber(max?.netAmount) ? record : max;
  }, null);

  return topCheck
    ? {
        checkId: topCheck.checkId,
        amount: safeNumber(topCheck.netAmount),
      }
    : null;
}

export function buildEmployeeRatingRows(records = []) {
  const grouped = groupRecordsByEmployee(records);
  const totalSummary = buildEmployeeSummary(records);
  const totalRevenue = totalSummary.revenue;

  const rows = Object.entries(grouped).map(([employeeId, employeeRecords]) => {
    const summary = buildEmployeeSummary(employeeRecords);
    const firstRecord = employeeRecords[0] || {};
    const pointTitle = joinTitles(employeeRecords.map((record) => record?.pointTitle));

    return {
      employeeId,
      employeeName: firstRecord?.employeeName || UNKNOWN_EMPLOYEE_NAME,
      employeeExternalId: firstRecord?.employeeExternalId || null,
      pointTitle,
      revenue: summary.revenue,
      revenueShare: totalRevenue ? roundNumber((summary.revenue / totalRevenue) * 100, 1) : 0,
      checksCount: summary.checksCount,
      averageCheck: summary.averageCheck,
      bonuses: summary.bonuses,
      bonusShare: totalRevenue ? roundNumber((summary.bonuses / totalRevenue) * 100, 1) : 0,
      guestRating: summary.guestRating,
    };
  });

  return rows.sort((left, right) => right.revenue - left.revenue);
}

export function buildEmployeeChecks(records = [], employeeId) {
  if (!employeeId) return [];
  const employeeKey = String(employeeId);

  return records
    .filter((record) => String(record?.employeeId ?? '') === employeeKey)
    .sort((left, right) => safeNumber(right?.paidAt) - safeNumber(left?.paidAt));
}

export function buildEmployeeDetails(records = [], employeeId, ratingRowsOverride = null, employeesById = null, options = {}) {
  const employeeRecords = buildEmployeeChecks(records, employeeId);
  const fallbackSummary = buildEmployeeSummary(employeeRecords);
  const ratingRows =
    Array.isArray(ratingRowsOverride) && ratingRowsOverride.length
      ? ratingRowsOverride
      : buildEmployeeRatingRows(records);
  const employeeKey = employeeId == null ? '' : String(employeeId);
  const employeeFromRefs = resolveEmployeeReference(employeesById, employeeKey);
  const ratingRow = ratingRows.find((row) => String(row?.employeeId ?? '') === employeeKey) || null;
  const checksTotal = Number(options.checksTotal);
  const preferRecordsSummary = Boolean(options.preferRecordsSummary);
  const summary = {
    ...(preferRecordsSummary ? fallbackSummary : buildEmployeeSummaryFromRatingRow(ratingRow, fallbackSummary)),
    ...(Number.isFinite(checksTotal) && checksTotal >= 0 ? { checksCount: checksTotal } : {}),
  };

  function rankBy(field, direction = 'desc') {
    const order = direction === 'asc' ? 1 : -1;
    const sorted = [...ratingRows].sort((a, b) => {
      const left = a?.[field];
      const right = b?.[field];
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      return (safeNumber(left) - safeNumber(right)) * order;
    });
    const idx = sorted.findIndex((row) => String(row?.employeeId ?? '') === employeeKey);
    return idx >= 0 ? idx + 1 : null;
  }

  const topCheck = buildTopCheck(employeeRecords);
  const topDish = buildTopDish(employeeRecords);

  return {
    employeeId,
    employeeName: resolveKnownEmployeeName(
      employeeRecords[0]?.employeeName,
      ratingRow?.employeeName,
      employeeFromRefs?.employeeName,
    ),
    pointTitle: joinTitles([
      ...employeeRecords.map((record) => record?.pointTitle),
      ratingRow?.pointTitle,
    ]),
    pointContextTitle: resolveEmployeePointContextTitle(employeeRecords, ratingRow, options),
    summary,
    topCheck,
    topDish,
    rankByRevenue: rankBy('revenue', 'desc'),
    rankByChecks: rankBy('checksCount', 'desc'),
    rankByAverageCheck: rankBy('averageCheck', 'desc'),
    rankByGuestRating: rankBy('guestRating', 'desc'),
  };
}

export function buildCheckDetails(records = [], checkId) {
  const record = records.find((item) => item?.checkId === checkId || item?.id === checkId) || null;
  if (!record) return null;
  let productIndex = 0;

  return {
    header: {
      checkId: record.checkId,
      orderId: record.orderId,
      checkNumber: record.checkNumber,
      pointTitle: record.pointTitle,
      employeeId: record.employeeId,
      employeeName: record.employeeName,
      paidAt: record.paidAt,
      amount: safeNumber(record.netAmount),
      tableNumber: record.tableNumber,
      guestRating: record.guestRating,
      guestReviewText: record.guestReviewText,
    },
    lines: (record.items || []).map((item) => ({
      index: item?.isModifier ? '' : ++productIndex,
      lineId: item.lineId,
      productName: item.productName,
      sku: item.sku,
      unitPrice: safeNumber(item.unitPrice),
      qty: item?.isDisplayOnly ? '' : safeNumber(item.qty),
      lineTotal: safeNumber(item.lineTotal),
      isModifier: Boolean(item?.isModifier),
      ...(item?.isDisplayOnly ? { isDisplayOnly: true } : {}),
      parentLineId: item?.parentLineId || null,
    })),
    totals: {
      grossAmount: safeNumber(record.grossAmount),
      discountAmount: safeNumber(record.discountAmount),
      bonusWriteOffAmount: safeNumber(record.bonusWriteOffAmount),
      bonusAccrualAmount: safeNumber(record.bonusAccrualAmount),
      netAmount: safeNumber(record.netAmount),
    },
  };
}
